import {
    AfterViewInit,
    Component,
    ElementRef,
    Injectable,
    OnDestroy,
    inject
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";

import {
    lastValueFrom
} from "rxjs";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogProgressBarComponent,
    TilbyDialogToolbarComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    subscribeInComponent
} from "src/app/shared/angular-utils";

import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { CommonModule } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";

import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";

import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";

import {
    ConnectionService
} from "src/app/core";

import {
    SatispayApiService,
    SatispayPayment
} from "src/app/features/digital-payments";

import {
    $state
} from "app/ajs-upgraded-providers";

import {
    QRCodeModule
} from "angularx-qrcode";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";

import {
    CustomerDisplayManagerService
} from "src/app/features";

import {
    MathUtils,
    removeFromArray
} from "src/app/shared/utils";

interface SatispayPaymentUI {
    id: string;
    amount_unit: number,
    disabled?: boolean,
    expired: boolean,
    insert_date: string,
    original_data: SatispayPayment
    removed?: boolean
    customer_amount: number,
    customer_name?: string,
    customer_image?: string
}

interface SatispayPaymentsOptions {
    amount: number;
    paymentMethodId: number;
}

export type SatispayPaymentsDialogData = { options: SatispayPaymentsOptions };

@Component({
    selector: "app-satispay-payments-dialog",
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatListModule,
        FormsModule,
        QRCodeModule,
        ReactiveFormsModule,
        TilbyCurrencyPipe,
        TilbyDialogContentComponent,
        TilbyDialogProgressBarComponent,
        TilbyDialogToolbarComponent,
        TranslateModule,
    ],
    providers: [],
    templateUrl: "./satispay-payments-dialog.component.html",
    styleUrls: ["./satispay-payments-dialog.component.scss"],
})
export class SatispayPaymentsDialogComponent implements AfterViewInit, OnDestroy {
    private readonly $state = inject($state);
    private readonly customerDisplayManager = inject(CustomerDisplayManagerService);
    private readonly elementRef = inject(ElementRef);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly satispayApi = inject(SatispayApiService);
    private readonly translateService = inject(TranslateService);

    protected readonly data: SatispayPaymentsDialogData = inject(MAT_DIALOG_DATA);

    private amountUnit = this.data.options.amount;
    private hasCompleted = false;
    private hasCustomerDisplay = this.customerDisplayManager.isColorDisplayEnabled() || !!this.customerDisplayManager.getNetworkDisplayIP();
    private paymentMethodId = this.data.options.paymentMethodId
    private paymentsById = new Map<string, SatispayPaymentUI>();

    protected amountToPay = MathUtils.round(this.amountUnit / 100);
    protected isOffline = false;
    protected kioskMode = this.$state.current.name.startsWith('app.kiosk');
    protected matchCodePayment?: SatispayPayment;
    protected message = '';
    protected setupForm?: FormGroup;
    protected operationInProgress = false;
    protected payments: SatispayPaymentUI[] = [];

    constructor() {
        subscribeInComponent(ConnectionService.connectionStatus$, (data) => {
            this.isOffline = !data.online;
        });
    }

    private safeTimeout(fn: () => void, ms: number) {
        if(this.hasCompleted) {
            return;
        }

        window.setTimeout(() => {
            if(!this.hasCompleted) {
                fn();
            }
        }, ms);
    }

    /**
     * Lifecycle hook that is called after a component's view has been fully initialized.
     * Fetches payments if not in kiosk mode.
     */
    ngAfterViewInit() {
        if (this.kioskMode) {
            this.createMatchCodePayment();
        } else {
            this.fetchPayments();
        }
    }

    private async createMatchCodePayment() {
        try {
            this.matchCodePayment = await this.satispayApi.createMatchCodePayment(this.paymentMethodId, this.amountUnit);

            if(this.hasCustomerDisplay && this.matchCodePayment.code_identifier) {
                this.customerDisplayManager.updateColorDisplay(undefined,  {
                    overrides: {
                        qrcode: {
                            url: this.matchCodePayment.code_identifier!,
                            text: ''
                        }
                    },
                    stringsOverrides: {
                        title_box_qr_code: this.translateService.instant('CASHREGISTER.SATISPAY_PAYMENTS.SCAN_QRCODE')
                    }
                });
            }

            this.safeTimeout(() => {
                this.fetchMatchCodePayment();
            }, 2000);
        } catch(error: any) {
            this.handleFetchError(error);
        }

    }

    private async fetchMatchCodePayment() {
        if(!this.matchCodePayment) {
            return;
        }

        this.matchCodePayment = await this.satispayApi.getPaymentById(this.paymentMethodId, this.matchCodePayment.id);

        switch(this.matchCodePayment.status) {
            case 'ACCEPTED':
                this.matDialogRef.close(this.matchCodePayment);
                break;
            case 'PENDING':
                this.safeTimeout(() => {
                    this.fetchMatchCodePayment();
                }, 2000);
                break;
            default:
                this.matDialogRef.close(undefined);
                break;
        }
    }

    /**
     * Lifecycle hook that is called when the component is destroyed.
     * Cancels any pending kiosk payment.
     */
    ngOnDestroy(): void {
        // Prevent timeout executions
        this.hasCompleted = true;

        // Cancel pending kiosk payment if it exists
        if (this.matchCodePayment) {
            if(this.matchCodePayment.status === 'PENDING') {
                this.satispayApi.cancelPayment(this.paymentMethodId, this.matchCodePayment.id);
            }

            if(this.hasCustomerDisplay) {
                this.customerDisplayManager.updateColorDisplay();
            }
        }
    }

    /**
     * Attempts to pair the shop with Satispay using the provided activation code.
     * Displays messages to the user regarding the pairing process and handles errors.
     */
    async pairShop() {
        if (!this.setupForm?.valid || this.operationInProgress) {
            return;
        }

        const activationCode = this.setupForm.get('activationCode')?.value;

        if (!activationCode) {
            return;
        }

        this.operationInProgress = true;
        this.message = this.translateService.instant('CASHREGISTER.SATISPAY_PAYMENTS.ENABLING');

        try {
            await this.satispayApi.setupShop(this.paymentMethodId, activationCode);
            this.message = this.translateService.instant('CASHREGISTER.SATISPAY_PAYMENTS.ENABLE_SUCCESSFUL');
            this.setupForm = undefined;

            this.safeTimeout(() => {
                this.deleteMessage();
                this.fetchPayments();
            }, 1500);
        } catch (error: any) {
            this.elementRef.nativeElement.querySelector('#SatispayCodeInput').focus();
            this.message = this.getErrorMessage(error);
        } finally {
            this.operationInProgress = false;
        }
    }

    /**
     * Retrieves an error message based on the provided error object.
     * @private
     * @param {any} error The error object.
     * @returns {string} The translated error message.
     */
    private getErrorMessage(error: any): string {
        if (!error || error.status === -1) {
            return '';
        }

        switch (error?.status) {
            case 404:
            case 403:
                return this.translateService.instant('CASHREGISTER.SATISPAY_PAYMENTS.INVALID_CODE');
            default:
                return this.translateService.instant('CASHREGISTER.SATISPAY_PAYMENTS.ENABLE_FAILED');
        }
    }

    /**
     * Clears the current message displayed to the user.
     */
    deleteMessage() {
        this.message = '';
    }

    /**
     * Fetches pending Satispay payments from the API.
     * Processes the received payments and sets up a recurring fetch.
     * Handles errors during the fetch process.
     * @private
     */
    private async fetchPayments() {
        try {
            const response = await this.satispayApi.getPendingPayments(this.paymentMethodId);

            if (response?.data) {
                this.processPayments(response.data);
            }

            if(this.hasCustomerDisplay && !this.matchCodePayment) {
                await this.createMatchCodePayment();
            }

            this.safeTimeout(() => {
                this.fetchPayments()
            }, 2000);
        } catch (error: any) {
            this.handleFetchError(error);
        }
    }

    /**
     * Processes a list of Satispay payments, updating the UI state.
     * This includes adding new payments, marking removed payments as disabled, and sorting the payments.
     * @private
     * @param {SatispayPayment[]} paymentsData The array of raw Satispay payment data.
     */
    private processPayments(paymentsData: SatispayPayment[]) {
        const paymentIdsSet = new Set<string>();

        //Add new payments
        for (const payment of paymentsData) {
            const uiPayment: SatispayPaymentUI = {
                id: payment.id,
                amount_unit: payment.amount_unit,
                expired: payment.expired,
                insert_date: payment.insert_date,
                original_data: payment,
                customer_amount: MathUtils.round(payment.amount_unit / 100),
            };

            if (uiPayment.expired || (this.amountUnit && uiPayment.amount_unit !== this.amountUnit)) {
                uiPayment.disabled = true;
            }

            if (payment.sender) {
                uiPayment.customer_name = payment.sender.name || '';

                const profilePictures = payment.sender.profile_pictures?.data;

                if (Array.isArray(profilePictures)) {
                    const profilePicture = profilePictures.find((pic) => pic.is_original) || profilePictures[0];

                    uiPayment.customer_image = profilePicture?.url;
                }
            }

            this.paymentsById.set(payment.id, uiPayment);
            paymentIdsSet.add(payment.id);
        }

        //Disable removed payments
        for (const [id, payment] of this.paymentsById.entries()) {
            if (!paymentIdsSet.has(id)) {
                payment.disabled = true;
                payment.removed = true;
            }
        }

        //Sort payments
        this.payments = [...this.paymentsById.values()].sort((a, b) => {
            const aSortValue = a.expired || (this.amountUnit && a.amount_unit !== this.amountUnit) ? Infinity : new Date(a.insert_date).getTime();
            const bSortValue = b.expired || (this.amountUnit && b.amount_unit !== this.amountUnit) ? Infinity : new Date(b.insert_date).getTime();

            return aSortValue - bSortValue;
        });

        //Remove the match code payment of the current session, since it should not be shown
        const matchCodePaymentId = this.matchCodePayment?.id;

        if(matchCodePaymentId) {
            removeFromArray(this.payments, (payment) => payment.id === matchCodePaymentId);
        }
    }

    /**
     * Creates a form for inputting a Satispay activation code.
     * @private
     */
    private createSetupForm() {
        this.setupForm = new FormGroup({
            activationCode: new FormControl('', [Validators.required])
        })
    }

    /**
     * Handles errors that occur during the fetching of Satispay payments.
     * @private
     * @param {any} error The error object.
     */
    private handleFetchError(error: any) {
        if (!error) {
            this.matDialogRef.close('UNKNOWN_ERROR');
            return;
        }

        switch (error.status) {
            case -1:
                this.safeTimeout(() => {
                    this.fetchPayments();
                }, 1000);
                break;
            case 403:
                if(!this.kioskMode) {
                    this.createSetupForm();
                } else {
                    this.matDialogRef.close('UNKNOWN_ERROR');
                }
                break;
            default:
                this.matDialogRef.close('UNKNOWN_ERROR');
                break;
        }
    }

    /**
     * Removes a Satispay payment by canceling it via the API and updating the local list of payments.
     * @param {SatispayPaymentUI} payment The UI representation of the Satispay payment to remove.
     */
    async removePayment(payment: SatispayPaymentUI) {
        this.operationInProgress = true;

        try {
            await this.satispayApi.cancelPayment(this.paymentMethodId, payment.id);
            this.paymentsById.delete(payment.id);

            this.payments = this.payments.filter(p => p.id !== payment.id);
        } finally {
            this.operationInProgress = false;
        }
    }

    /**
     * Selects a Satispay payment and closes the dialog with the selected payment.
     * The payment can only be selected if it is not disabled.
     * @param {SatispayPaymentUI} payment The Satispay payment to select.
     */
    selectPayment(payment: SatispayPaymentUI) {
        if (!payment.disabled) {
            this.matDialogRef.close(payment.original_data);
        }
    }
}

@Injectable({
    providedIn: "root",
})
export class SatispayPaymentsDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    /**
     * Opens the Satispay Payments Dialog.
     * @param {NonNullableConfigData<SatispayPaymentsDialogData>} config - The configuration for the dialog.
     * @returns {Promise<SatispayPaymentUI | undefined>} A promise that resolves with the selected Satispay payment UI object, a string if an error occurs, or undefined if the dialog is closed.
     */
    public async openDialog(config: NonNullableConfigData<SatispayPaymentsDialogData>): Promise<SatispayPayment | string | undefined> {
        const configEdited: NonNullableConfigData<SatispayPaymentsDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions({ width: '600px' }),
            disableClose: true,
            data: config.data,
        };

        return await lastValueFrom(
            this.dialogRef.open(SatispayPaymentsDialogComponent, configEdited).afterClosed()
        ) || undefined;
    }
}